// find: the entire file content
// replace: entire file with the below

import React, { useEffect, useState } from "react";
import {
  Spin,
  Table,
  Button,
  InputNumber,
  Space,
  Popconfirm,
  Row,
  Col,
  Select,
  Segmented,
  DatePicker,
  Input,
  Form,
  Typography,
} from "antd";
import { ExpenseServices } from "../../Services/Manage/ExpenseServices";
import { PlusOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { UserService } from "../../Services/Manage/UserService";
import useMessage from "antd/es/message/useMessage";
import dayjs from "dayjs";

const CreatePayment = ({ quotationNo, grandTotal }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false); // 🔹 track prePayments
  const [message, msgContex] = useMessage();

  // 🔹 Payment mode: "full" or "installment"
  const [paymentMode, setPaymentMode] = useState("full");
  const [isFullPaymentRecord, setIsFullPaymentRecord] = useState(false);

  // 🔹 Full payment state
  const [fullPayment, setFullPayment] = useState({
    date: dayjs(),
    amount: 0,
    pay_mode: undefined,
    notes: "",
  });

  useEffect(() => {
    const fetchPayments = async () => {
      try {

        if (!quotationNo) {
          setRows([]);
          setIsEditMode(false);
          return;
        }

        setLoading(true);
        const response = await ExpenseServices.getPaymentByQuId(quotationNo);

        const payments = (response?.data || []).map((p) => ({
          ...p,
          key: p.id || Date.now() + Math.random(),
          paid_amt: Number(p.paid_amt) || 0,
          payment_date: p.payment_date ? dayjs(p.payment_date) : dayjs(),
          notes: p.notes || "",
        }));

        if (payments.length > 0) {
          setIsEditMode(true); // 🔹 means prepayments exist
          // If exactly one payment exists and it covers the grand total, treat as full payment record for editing convenience
          if (payments.length === 1 && Number(payments[0].paid_amt) >= Number(grandTotal)) {
            setPaymentMode("full");
            setIsFullPaymentRecord(true);
            setFullPayment({
              date: payments[0].payment_date,
              amount: payments[0].paid_amt,
              pay_mode: payments[0].pay_mode,
              notes: payments[0].notes,
              id: payments[0].id,
            });
          } else {
            setPaymentMode("installment");
            setIsFullPaymentRecord(false);
          }
        } else {
          // No existing payments for this record — reset everything to defaults
          // so a previous invoice's payment data doesn't linger on screen.
          setIsEditMode(false);
          setIsFullPaymentRecord(false);
          setPaymentMode("full");
          setFullPayment({
            date: dayjs(),
            amount: 0,
            pay_mode: undefined,
            notes: "",
          });
        }

        setRows(payments);
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    };

    if (quotationNo) fetchPayments();
  }, [quotationNo]);

  const getRunningTotal = () =>
    rows.reduce((sum, row) => sum + (Number(row.paid_amt) || 0), 0);

  const handleAddRow = () => {
    const runningTotal = getRunningTotal();
    if (runningTotal >= grandTotal) {
      message.warning("Grand total already fully paid!");
      return;
    }

    setRows([
      ...rows,
      {
        key: Date.now(),
        paid_amt: 0,
        payment_date: dayjs(), // 🔹 default to today
        notes: "",
      },
    ]);
  };
  const handleDelete = async (key) => {
    const target = rows.find((r) => r.key === key);
    try {
      if (target?.id) {
        const request = await ExpenseServices.deletePayment(target.id);
        if (!request?.status) {
          message.error(request?.message || "Failed to delete payment");
          return;
        }
        message.success("Payment deleted");
      }
      const remaining = rows.filter((row) => row.key !== key);
      setRows(remaining);
      if (remaining.length === 0) {
        setIsEditMode(false);
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong while deleting the payment");
    }
  };
  const handleChange = (value, key) => {
    const runningTotal = getRunningTotal();
    const currentRow = rows.find((r) => r.key === key);

    // 🔹 Remaining balance before editing this row
    const balanceBefore = grandTotal - (runningTotal - (currentRow?.paid_amt || 0));

    // 🔹 Validation
    if (value < 0) {
      message.warning("Installment cannot be negative!");
      return;
    }
    if (value > balanceBefore) {
      message.warning(`Installment cannot exceed remaining balance (${balanceBefore})`);
      return;
    }

    setRows(
      rows.map((row) =>
        row.key === key ? { ...row, paid_amt: Number(value) || 0 } : row
      )
    );
  };

  const handlePayModeChange = (value, key) => {
    setRows(
      rows.map((row) =>
        row.key === key ? { ...row, pay_mode: value } : row
      )
    );
  };

  const handleDateChange = (value, key) => {
    setRows(
      rows.map((row) =>
        row.key === key ? { ...row, payment_date: value || dayjs() } : row
      )
    );
  };

  const handleNotesChange = (value, key) => {
    setRows(
      rows.map((row) =>
        row.key === key ? { ...row, notes: value } : row
      )
    );
  };

  const handleSaveInstallments = async () => {
    try {
      if (rows.length === 0) {
        message.warning("No payments to save!");
        return;
      }
      const user = await UserService.getOmeUser();

      let runningTotal = 0;
      const result = rows.map((row) => {
        runningTotal += row.paid_amt;
        const balance = Math.max(grandTotal - runningTotal, 0);

        return {
          id: row.id,
          added_by: user.uid,
          qu_no: quotationNo,
          paid_amt: row.paid_amt,
          balance,
          status: balance === 0 ? "Paid" : "Pending",
          grand_total: grandTotal,
          pay_mode: row.pay_mode,
          payment_date: row.payment_date ? dayjs(row.payment_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
          notes: row.notes || "",
        };
      });

      console.log("Save Payload:", result);

      let request;
      if (isEditMode) {
        request = await ExpenseServices.updatePayment(result);
      } else {
        request = await ExpenseServices.createPayment(result);
      }

      if (request?.status) {
        message.success(request.message || "Payments saved successfully!");
      } else {
        message.error(request?.message || "Failed to save payments");
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong while saving payments");
    }
  };

  const handleSaveFullPayment = async () => {
    try {
      if (!fullPayment.amount || fullPayment.amount <= 0) {
        message.warning("Please enter a valid amount!");
        return;
      }
      if (fullPayment.amount > grandTotal) {
        message.warning(`Amount cannot exceed grand total (${grandTotal})`);
        return;
      }

      const user = await UserService.getOmeUser();
      const balance = Math.max(grandTotal - fullPayment.amount, 0);

      const payload = [{
        id: fullPayment.id,
        added_by: user.uid,
        qu_no: quotationNo,
        paid_amt: fullPayment.amount,
        balance,
        status: balance === 0 ? "Paid" : "Pending",
        grand_total: grandTotal,
        pay_mode: fullPayment.pay_mode,
        payment_date: fullPayment.date ? dayjs(fullPayment.date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
        notes: fullPayment.notes || "",
      }];

      console.log("Save Full Payment Payload:", payload);

      let request;
      if (isEditMode || fullPayment.id) {
        request = await ExpenseServices.updatePayment(payload);
      } else {
        request = await ExpenseServices.createPayment(payload);
      }

      if (request?.status) {
        message.success(request.message || "Payment saved successfully!");
      } else {
        message.error(request?.message || "Failed to save payment");
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong while saving the payment");
    }
  };

  const columns = [
    {
      title: "Date",
      key: "payment_date",
      width: 150,
      render: (_, record) => (
        <DatePicker
          size="large"
          style={{ width: "100%" }}
          format="DD-MM-YYYY"
          value={record.payment_date ? dayjs(record.payment_date) : dayjs()}
          onChange={(value) => handleDateChange(value || dayjs(), record.key)}
        />
      ),
    },
    {
      title: "Installment Amount",
      dataIndex: "paid_amt",
      key: "paid_amt",
      render: (text, record) => {
        const runningTotalExcludingCurrent = rows.reduce(
          (sum, r) => sum + (r.key === record.key ? 0 : (Number(r.paid_amt) || 0)),
          0
        );
        const maxBalance = Math.max(grandTotal - runningTotalExcludingCurrent, 0);

        return (
          <InputNumber
            size="large"
            min={0}
            max={maxBalance}
            style={{ width: "100%" }}
            value={record.paid_amt}
            onChange={(value) => handleChange(value, record.key)}
          />
        );
      },
    },
    {
      title: "Balance",
      key: "balance",
      render: (_, __, index) => {
        let runningTotal = rows
          .slice(0, index + 1)
          .reduce((sum, r) => sum + (Number(r.paid_amt) || 0), 0);
        return Math.max(grandTotal - runningTotal, 0);
      },
    },
    {
      title: "Pay Mode",
      key: "pay_mode",
      render: (_, record) => (
        <Select
          size="large"
          value={record.pay_mode}
          onChange={(val) => handlePayModeChange(val, record.key)}
          placeholder="Select Pay Mode"
          style={{ width: "100%" }}
          options={[
            { label: "Cash", value: "cash" },
            { label: "Card Payment", value: "card" },
            { label: "Bank Transfer", value: "bank" },
            { label: "Online Payment", value: "onlinePay" },
          ]}
        />
      ),
    },
    {
      title: "Notes",
      key: "notes",
      width: 200,
      render: (_, record) => (
        <Input
          size="large"
          placeholder="Add notes..."
          value={record.notes}
          onChange={(e) => handleNotesChange(e.target.value, record.key)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Popconfirm
          title="Remove this installment?"
          onConfirm={() => handleDelete(record.key)}
        >
          <Button danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  if (loading) return <Spin />;

  return (
    <div>
      {msgContex}

      <Row style={{ marginBottom: 16 }}>
        <Segmented
          size="large"
          value={paymentMode}
          onChange={setPaymentMode}
          options={[
            {
              label: isFullPaymentRecord ? "Full Payment (Paid)" : "Full Payment",
              value: "full",
              disabled: isEditMode && !isFullPaymentRecord, // installments already exist — lock out full payment
            },
            {
              label: "Add Installment",
              value: "installment",
              disabled: isFullPaymentRecord, // a full payment record already exists — lock out installments
            },
          ]}
        />
        {/* {isFullPaymentRecord && (
          <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
            This invoice was fully paid as a single payment. Switch isn't allowed — delete the payment first if you need installments instead.
          </Typography.Text>
        )} */}
        {/* {isEditMode && !isFullPaymentRecord && rows.length > 0 && (
          <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
            This invoice already has installments recorded. Full Payment is disabled — delete all installments first if you need to switch.
          </Typography.Text>
        )} */}
      </Row>
      {paymentMode === "full" ? (
        <div>
          <h3 style={{ marginBottom: 16 }}>
            Grand Total: {grandTotal}
          </h3>
          <Form layout="vertical">
            <Row gutter={16}>
              <Col flex="1">
                <Form.Item label="Payment Date">
                  <DatePicker
                    size="large"
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    value={fullPayment.date ? dayjs(fullPayment.date) : dayjs()}
                    onChange={(value) => setFullPayment((p) => ({ ...p, date: value || dayjs() }))}
                  />
                </Form.Item>
              </Col>

              <Col flex="1">
                <Form.Item label="Amount">
                  <InputNumber
                    size="large"
                    min={0}
                    max={grandTotal}
                    style={{ width: "100%" }}
                    value={fullPayment.amount}
                    onChange={(value) => setFullPayment((p) => ({ ...p, amount: Number(value) || 0 }))}
                  />
                </Form.Item>
              </Col>

              <Col flex="1">
                <Form.Item label="Pay Mode">
                  <Select
                    size="large"
                    value={fullPayment.pay_mode}
                    onChange={(val) => setFullPayment((p) => ({ ...p, pay_mode: val }))}
                    placeholder="Select Pay Mode"
                    style={{ width: "100%" }}
                    options={[
                      { label: "Cash", value: "cash" },
                      { label: "Card Payment", value: "card" },
                      { label: "Bank Transfer", value: "bank" },
                      { label: "Online Payment", value: "onlinePay" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Notes">
              <Input.TextArea
                rows={2}
                placeholder="Add notes for this payment..."
                value={fullPayment.notes}
                onChange={(e) => setFullPayment((p) => ({ ...p, notes: e.target.value }))}
              />
            </Form.Item>
            <Space>
              <Button
                size="large"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveFullPayment}
              >
                {fullPayment.id ? "Update" : "Save"}
              </Button>
              {fullPayment.id && (
                <Popconfirm
                  title="Delete this payment?"
                  onConfirm={async () => {
                    try {
                      const request = await ExpenseServices.deletePayment(fullPayment.id);
                      if (request?.status) {
                        message.success("Payment deleted");
                        setIsFullPaymentRecord(false);
                        setIsEditMode(false);
                        setRows([]);
                        setFullPayment({ date: dayjs(), amount: 0, pay_mode: undefined, notes: "" });
                      } else {
                        message.error(request?.message || "Failed to delete payment");
                      }
                    } catch (error) {
                      console.error(error);
                      message.error("Something went wrong while deleting the payment");
                    }
                  }}
                >
                  <Button size="large" danger icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form>
        </div>

      ) : (
        <div>
          <Row style={{ justifyContent: "space-between" }}>
            <h3>
              Grand Total: {grandTotal} | Paid: {getRunningTotal()} | Remaining:{" "}
              {grandTotal - getRunningTotal()}
            </h3>

            <Space style={{ marginBottom: 16 }}>
              <Button
                size="large"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddRow}
              >
                Add Installment
              </Button>
              <Button
                size="large"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveInstallments}
              >
                {isEditMode ? "Update" : "Save"}
              </Button>
            </Space>
          </Row>
          <Table
            rowKey="key"
            dataSource={isFullPaymentRecord ? [] : rows}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
          />
        </div>
      )}
    </div>
  );
};
export default CreatePayment;